import { TokenAmount, Pair, Currency, Token } from '@uniswap/sdk'
import { useMemo } from 'react'
import { abi as IUniswapV2PairABI } from '@uniswap/v2-core/build/IUniswapV2Pair.json'
import { Interface } from '@ethersproject/abi'
import { useActiveWeb3React } from '../hooks'

import { useMultipleContractSingleData, useSingleContractMultipleData } from '../state/multicall/hooks'
import { wrappedCurrency } from '../utils/wrappedCurrency'
import { useV2FactoryContract } from '../hooks/useContract'

const PAIR_INTERFACE = new Interface(IUniswapV2PairABI)

export enum PairState {
  LOADING,
  NOT_EXISTS,
  EXISTS,
  INVALID
}

export function usePairs(currencies: [Currency | undefined, Currency | undefined][]): [PairState, Pair | null][] {
  const { chainId } = useActiveWeb3React()
  const factoryContract = useV2FactoryContract()

  const tokens = useMemo(
    () =>
      currencies.map(([currencyA, currencyB]) => [
        wrappedCurrency(currencyA, chainId),
        wrappedCurrency(currencyB, chainId)
      ]),
    [chainId, currencies]
  )

  // Create call inputs for factory.getPair(tokenA, tokenB)
  const pairCallInputs = useMemo(
    () =>
      tokens.map(([tokenA, tokenB]) => {
        if (!tokenA || !tokenB || tokenA.equals(tokenB)) return undefined
        const [token0, token1] = tokenA.sortsBefore(tokenB) ? [tokenA, tokenB] : [tokenB, tokenA]
        return [token0.address, token1.address]
      }),
    [tokens]
  )

  // Fetch pair addresses from factory contract
  const pairAddressResults = useSingleContractMultipleData(
    factoryContract,
    'getPair',
    pairCallInputs.filter((input): input is [string, string] => input !== undefined)
  )

  // Map results back to pair addresses, handling undefined inputs
  const pairAddresses = useMemo(() => {
    let resultIndex = 0
    return pairCallInputs.map(input => {
      if (!input) return undefined
      const result = pairAddressResults[resultIndex++]
      if (!result || result.loading || !result.result) return undefined
      const address = result.result[0]
      // Check if address is zero (pair doesn't exist)
      if (!address || address === '0x0000000000000000000000000000000000000000') return undefined
      return address as string
    })
  }, [pairCallInputs, pairAddressResults])

  const results = useMultipleContractSingleData(pairAddresses, PAIR_INTERFACE, 'getReserves')

  return useMemo(() => {
    return results.map((result, i) => {
      const { result: reserves, loading } = result
      const tokenA = tokens[i][0]
      const tokenB = tokens[i][1]

      if (loading) return [PairState.LOADING, null]
      if (!tokenA || !tokenB || tokenA.equals(tokenB)) return [PairState.INVALID, null]
      if (!reserves) return [PairState.NOT_EXISTS, null]
      const { reserve0, reserve1 } = reserves
      const [token0, token1] = tokenA.sortsBefore(tokenB) ? [tokenA, tokenB] : [tokenB, tokenA]

      const pair = new Pair(new TokenAmount(token0, reserve0.toString()), new TokenAmount(token1, reserve1.toString()))

      // Override the liquidityToken with the correct address from factory
      // The SDK's Pair.getAddress() uses hardcoded INIT_CODE_HASH which doesn't match our factory
      const pairAddress = pairAddresses[i]
      if (pairAddress) {
        Object.defineProperty(pair, 'liquidityToken', {
          value: new Token(token0.chainId, pairAddress, 18, 'MINAX-LP', 'Minax DEX LP'),
          writable: false
        })
      }

      return [PairState.EXISTS, pair]
    })
  }, [results, tokens, pairAddresses])
}

export function usePair(tokenA?: Currency, tokenB?: Currency): [PairState, Pair | null] {
  return usePairs([[tokenA, tokenB]])[0]
}
